import type { ClientJournalTemplate, GlAccount, TemplateLine, TemplateLineSide } from '../types';
import { sameName } from './glOneList';

/**
 * The standard payroll template: Payroll Relief's GL Accounts roles (ptc-docs plan 2026-11-04-conversions-wave-5-payroll
 * §2.1). The same list as `api/Models/PayrollRoles.cs`, in the same order; the side is the role's in a payroll journal (pay
 * leaves the bank, so cash is a credit). Each role's words are `glMappingPage.fields.<key>`; its group's are
 * `glMappingPage.payroll.groups.<group>`.
 */
export type PayrollGroup = 'cash' | 'clearing' | 'compensation' | 'federal' | 'stateLocal' | 'deductions' | 'expenses';

export interface PayrollRole {
  key: string;
  group: PayrollGroup;
  side: TemplateLineSide;
  /** Account names a client's chart often uses for the role: "Start from the standard" maps a role to such an account. */
  aliases: readonly string[];
}

const r = (key: string, group: PayrollGroup, side: TemplateLineSide, ...aliases: string[]): PayrollRole => ({ key, group, side, aliases });

export const PAYROLL_ROLES: readonly PayrollRole[] = [
  r('prCashPayroll', 'cash', 'credit', 'P/R checking', 'payroll checking', 'payroll account'),
  r('prCashRegular', 'cash', 'credit', 'Regular checking', 'operating account'),
  r('prClearing', 'clearing', 'credit', 'Payroll clearing', 'payroll clearing account'),
  r('prOfficerSalary', 'compensation', 'debit', 'Officer salary', 'officer salaries', 'officers salary', 'officer compensation'),
  r('prWages', 'compensation', 'debit', 'Wages', 'salaries and wages', 'wages and salaries', 'payroll wages'),
  r('prBonus', 'compensation', 'debit', 'Bonus', 'bonuses'),
  r('prCommissions', 'compensation', 'debit', 'Commissions', 'commission'),
  r('prPaycheckTips', 'compensation', 'debit', 'Paycheck tips', 'tips paid'),
  r('prNonEmployeeComp', 'compensation', 'debit', 'Non-employee compensation', 'contract labor', 'contractors', '1099 contractors'),
  r('prFederalWh', 'federal', 'credit', 'Federal W/H', 'federal withholding', 'federal income tax withheld'),
  r('prSocSecWh', 'federal', 'credit', 'Soc Sec W/H', 'social security withheld'),
  r('prMedicareWh', 'federal', 'credit', 'Medicare W/H', 'medicare withheld'),
  r('prFutaPayable', 'federal', 'credit', 'FUTA payable', 'futa liability'),
  r('prStateWh', 'stateLocal', 'credit', 'State W/H', 'state withholding'),
  r('prLocalWh', 'stateLocal', 'credit', 'Local W/H', 'local withholding'),
  r('prDisabilityWh', 'stateLocal', 'credit', 'Disability W/H', 'sdi withheld'),
  r('prUnemploymentWh', 'stateLocal', 'credit', 'Unemployment W/H'),
  r('prSuiPayable', 'stateLocal', 'credit', 'SUI payable', 'sui liability', 'suta payable', 'state unemployment payable'),
  r('prOtherTaxesPayable', 'stateLocal', 'credit', 'Other taxes payable'),
  r('prWorkersCompPayable', 'stateLocal', 'credit', "Workers' comp payable", 'workers compensation payable'),
  r('prDeductions', 'deductions', 'credit', 'Deductions', 'payroll deductions', 'employee deductions'),
  r('prSocSecExpense', 'expenses', 'debit', 'Soc Sec expense', 'social security expense', 'fica expense'),
  r('prMedicareExpense', 'expenses', 'debit', 'Medicare expense'),
  r('prFutaExpense', 'expenses', 'debit', 'FUTA expense'),
  r('prSutaExpense', 'expenses', 'debit', 'SUTA expense', 'sui expense', 'state unemployment expense'),
  r('prDisabilityExpense', 'expenses', 'debit', 'Disability expense'),
  r('prWorkersCompExpense', 'expenses', 'debit', "Workers' comp expense", 'workers compensation', 'workers compensation insurance'),
  r('prOtherExpense', 'expenses', 'debit', 'Other payroll expense', 'payroll taxes'),
  r('prServiceFee', 'expenses', 'debit', 'Payroll service', 'payroll service fees', 'payroll processing fees'),
  r('prAllowances', 'expenses', 'debit', 'Allowances'),
  r('prReimbursement', 'expenses', 'debit', 'Reimbursement', 'reimbursements', 'employee reimbursements'),
  r('prSuspense', 'expenses', 'debit', 'Payroll suspense', 'suspense'),
];

export const PAYROLL_GROUPS: readonly PayrollGroup[] = ['cash', 'clearing', 'compensation', 'federal', 'stateLocal', 'deductions', 'expenses'];

export const PAYROLL_KEYS: ReadonlySet<string> = new Set(PAYROLL_ROLES.map((x) => x.key));

/** A contractor (1099) run's two roles (§2.2): non-employee comp against clearing. */
export const CONTRACTOR_COMP = 'prNonEmployeeComp';
export const PAYROLL_CLEARING = 'prClearing';

export const payrollRole = (key: string): PayrollRole | undefined => PAYROLL_ROLES.find((x) => x.key === key);

/** The client's payroll template, or null when it has none yet. */
export function ownPayrollTemplate(t: ClientJournalTemplate | null | undefined): TemplateLine[] | null {
  return t?.lines?.length ? t.lines : null;
}

/**
 * "Start from the standard payroll template" (§2.1): every role, in Payroll Relief's order, on its own side. A role whose
 * account the client's chart already names (an active account called "Payroll clearing", say) is proposed with it, unless
 * the role is mapped already, which is kept. Every other role is left unmapped, for the bookkeeper to pick or remove.
 */
export function proposeStandardPayroll(
  roles: Readonly<Record<string, string>>,
  accounts: readonly GlAccount[],
): { template: ClientJournalTemplate; roles: Record<string, string>; matched: number } {
  const next = { ...roles };
  let matched = 0;
  const taken = new Set(Object.values(roles).map((c) => c.trim().toLowerCase()));
  const active = accounts.filter((a) => a.status !== 'ARCHIVED');
  for (const role of PAYROLL_ROLES) {
    if ((next[role.key] ?? '').trim()) continue;
    const names = new Set(role.aliases.map(sameName));
    const hit = active.find((a) => {
      const n = sameName(a.nameOverride?.trim() || a.name?.trim() || '');
      return n !== '' && names.has(n) && !taken.has(a.code.trim().toLowerCase());
    });
    if (hit) {
      next[role.key] = hit.code;
      taken.add(hit.code.trim().toLowerCase());
      matched++;
    }
  }
  const lines: TemplateLine[] = PAYROLL_ROLES.map((role) => ({ key: role.key, side: role.side, source: 'manual' }));
  return { template: { lines }, roles: next, matched };
}

/** Removes a payroll line and its mapping (a line with no figure posts nothing, so removing it is tidying, not a rule). */
export function removePayrollLine(
  template: ClientJournalTemplate,
  roles: Readonly<Record<string, string>>,
  key: string,
): { template: ClientJournalTemplate | null; roles: Record<string, string> } {
  const lines = template.lines.filter((l) => l.key !== key);
  const { [key]: _gone, ...rest } = roles;
  return { template: lines.length ? { lines } : null, roles: rest };
}

/** The lines a payroll journal's boxes show for a run (§2.2): a contractor run, non-employee comp alone; a regular run, the rest. */
export function boxesForRun(lines: readonly TemplateLine[], kind: 'regular' | 'contractor'): TemplateLine[] {
  if (kind === 'contractor') {
    return [lines.find((l) => l.key === CONTRACTOR_COMP) ?? { key: CONTRACTOR_COMP, side: 'debit', source: 'manual' }];
  }
  return lines.filter((l) => l.key !== CONTRACTOR_COMP);
}
